import Skyflow from 'skyflow-js';
import { Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import { Alert, Button, Input } from '@studykik/ui-library';
import { isAxiosError } from 'axios';
import React, { PropsWithChildren, useMemo, useState, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoadingSpinnerContext } from '../../contexts/loading-spinner-context';
import { usePatientContext } from '../../contexts/patient-context';
import { environment } from '../../environments/environment';
import { IAddress } from '../../models';
import { Patient } from '../../models/patient';
import { isDateValid, padNumber } from '../../utils/isDateValid';
import { AddressAutocomplete } from '../address-autocomplete';
import {
  CloseBtn,
  Description,
  FieldComponent,
  FieldInline,
  FieldLabel,
  FieldsWrapper,
  Header,
  ModalWrapper,
  SubmitWrapper,
  Title,
  TopBox,
} from '../layout/styles';
import {
  isAnyFieldVisible,
  isAnyRequiredFieldMissing,
} from '../patient-guard/utils';
import { getSkyflowClient, IUpdatePatientPayload } from '../../utils/skyflow/skyflowClient';
import { SkyflowIMSPatient } from '../../utils/skyflow/types';
import formatAddress from '../../utils/formatAddress';
import { JWT_LOCAL_STORAGE_KEY } from '../../constants/constants';

interface SubmitDOBData {
  day: string;
  month: string;
  year: string;
}

const getInitialAddress = (address?: Patient | IAddress | null): IAddress | undefined => {
  // autofilled by autocomplete previously
  if (address && address.city) {
    return {
      address: address.address || '',
      address2: address.address2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      country: address.country || '',
    };
  }

  // manual entry
  if (address && address.address) {
    return {
      address: address.address,
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    };
  }

  return undefined;
};

const MAX_SKYFLOW_RETRIES = 2;

const PatientsDetailsFormPageComponent: React.FC<PropsWithChildren> = () => {
  const { patient, updatePatient } = usePatientContext();
  const {
    setShowingLoadingSpinner,
    isShowingLoadingSpinnerDict,
    isShowing: isLoading,
  } = useLoadingSpinnerContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isPatientInEU, setIsPatientInEU] = useState<boolean | null>(null);
  const [wasDeobfuscated, setWasDeobfuscated] = useState<boolean>(false);
  const [skyflowRetries, setSkyflowRetries] = useState<number>(0);
  const [address, setAddress] = useState<IAddress | null>(
    patient?.address
      ? {
        address: patient.address,
        address2: patient?.address2 || '',
        city: patient?.city || '',
        state: patient?.state || '',
        zip: patient?.zip || '',
        country: patient?.country || '',
      }
      : null
  );
  const [addressInteraction, setAddressInteraction] = useState<boolean>(false);

  const {
    control,
    formState: { isDirty, touchedFields },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      day: (patient?.dateOfBirth &&
        padNumber(new Date(patient?.dateOfBirth).getUTCDate()) || ''),
      month: (patient?.dateOfBirth &&
        padNumber(new Date(patient?.dateOfBirth).getUTCMonth() + 1) || ''),
      year: (patient?.dateOfBirth &&
        padNumber(new Date(patient?.dateOfBirth).getUTCFullYear()) || ''),
    },
  });

  const hasBeenTouched = Object.values(touchedFields).length > 0;

  const [day, month, year] = useWatch({
    control,
    name: ['day', 'month', 'year'],
  });

  const onSubmit = async (data: SubmitDOBData) => {
    if (patient?.vaultId && (patient.address || patient.dateOfBirth) && !wasDeobfuscated) {
      return;
    }

    setShowingLoadingSpinner('patientUpdateForm', true);
    let payload: Partial<Patient> = {};

    if (data.year && data.month && data.day) {
      payload = {
        ...payload,
        dateOfBirth: `${data.year}-${padNumber(+data.month)}-${padNumber(
          +data.day
        )}`,
      };
    }

    if (
      address?.address &&
      typeof address.address === 'string' &&
      address?.address.length > 0
    ) {
      payload = {
        ...payload,
        ...address,
      };
    }

    try {
      let patientVaultData = {};
      if (patient?.vaultId) {
        const accessToken = localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
        const sfClient = getSkyflowClient({
          authToken: accessToken as string,
        });
        await sfClient.update<SkyflowIMSPatient>(
          {
            patientId: patient.id,
            address: payload.address,
            address_2: payload.address2,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            dob: payload.dateOfBirth,
          } as IUpdatePatientPayload,
        );
        const response = await sfClient.get<SkyflowIMSPatient>(
          patient.vaultId,
          Skyflow.RedactionType.MASKED
        );
        const {
          address: address1,
          address_2: address2,
          city,
          state,
          zip,
          dob,
        } = response.records[0].fields;
        if (data.year && data.month && data.day) {
          patientVaultData = {
            ...patientVaultData,
            dateOfBirth: dob.replace(/XX/g, "01"),
          };
        }
        if (
          address?.address &&
          typeof address.address === 'string' &&
          address?.address.length > 0
        ) {
          patientVaultData = {
            ...patientVaultData,
            address: address1,
            city,
            zip,
            address2: address2 || '',
            state: state || '',
          };
        }
      }
      await updatePatient(patient!.id!, {
        ...payload,
        ...patientVaultData,
        isEuPatient: !!Object.keys(patientVaultData).length,
      });
      setTimeout(() => navigate('/'), 500);
    } catch (e) {
      if (isAxiosError(e)) {
        toast.error(e.response?.data?.message ?? t('Failed'));
      }
    } finally {
      setShowingLoadingSpinner('patientUpdateForm', false);
    }
  };

  const isValid = useMemo(() => {
    const dateString = `${year}-${padNumber(+month)}-${padNumber(+day)}`;
    const patientForm = {
      ...patient,
      address: address?.address,
    };
    patientForm.dateOfBirth = isDateValid({ year, month, day })
      ? dateString
      : undefined;
    if (isAnyRequiredFieldMissing(patientForm)) {
      return false;
    }

    return true;
  }, [isDirty, patient, day, month, year, address]);

  const unobfuscatePatientData = async () => {
    if (wasDeobfuscated) return;
    setShowingLoadingSpinner('getVaultPatientDetails', true);
    try {
      const accessToken = localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
      if (!accessToken || !patient?.vaultId) {
        throw new Error('User not found or patient vaultId not found');
      }
      const sfClient = getSkyflowClient({
        authToken: accessToken as string,
      });
      const response = await sfClient.get<SkyflowIMSPatient>(
        patient?.vaultId as string,
      );
      const unobfuscatedData = response.records[0].fields;
      const dobDate = new Date(unobfuscatedData.dob);
      setAddress({
        address: unobfuscatedData.address,
        address2: unobfuscatedData.address_2,
        city: unobfuscatedData.city,
        state: unobfuscatedData.state,
        zip: unobfuscatedData.zip,
        country: patient.country,
      });
      reset({
        day: padNumber(dobDate.getUTCDate()),
        month: padNumber(dobDate.getUTCMonth() + 1),
        year: padNumber(dobDate.getUTCFullYear()),
      });
      setWasDeobfuscated(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error unobfuscating patient data', error);
      setSkyflowRetries((retries) => retries + 1);
    } finally {
      setShowingLoadingSpinner('getVaultPatientDetails', false);
    }
  };

  useEffect(() => {
    const localizationCheck = async () => {
      setShowingLoadingSpinner('localizationCheck', true);
      try {
        const accessToken = localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
        const sfClient = getSkyflowClient({
          authToken: accessToken as string,
        });
        await sfClient.init();
        setIsPatientInEU(true);
        // eslint-disable-next-line no-console
        console.info("Patient is in EU");
      } catch (error) {
        setIsPatientInEU(false);
        // eslint-disable-next-line no-console
        console.info("Patient is not in EU", error);
      } finally {
        setShowingLoadingSpinner('localizationCheck', false);
      }
    };
    if (patient?.vaultId) {
      localizationCheck();
    }
  }, []);

  useEffect(() => {
    if (
      !wasDeobfuscated &&
      !isShowingLoadingSpinnerDict?.getVaultPatientDetails &&
      patient?.vaultId &&
      (patient?.dateOfBirth ||
        patient?.address) &&
      skyflowRetries < MAX_SKYFLOW_RETRIES
    ) {
      unobfuscatePatientData();
    }
  }, [patient, isShowingLoadingSpinnerDict, wasDeobfuscated, skyflowRetries]);

  const resetForm = () => {
    reset();
    navigate('/');
  };

  const description = patient?.patientDetailsConfig?.dob?.visible ? (
    <Description>
      {t('Please enter your date of birth below to complete the account.')}
    </Description>
  ) : null

  const parsedAddressValue = useMemo(() => formatAddress(
    address as IAddress
  ), [address]);

  return (
    <ModalWrapper data-testid="patient-details-form-page">
      <TopBox>
        <Box>
          <Title>{t('Personal Details')}</Title>
        </Box>
        <Box marginLeft="auto">
          {!isAnyRequiredFieldMissing(patient) && (
            <CloseBtn
              role="button"
              tabIndex={0}
              onClick={resetForm}
              onKeyPress={resetForm}
              className="close"
            >
              <CloseIcon />
            </CloseBtn>
          )}
        </Box>
      </TopBox>
      {isAnyFieldVisible(patient) ? description : (
        <Header>
          <img
            style={{ height: '40px', width: '100%' }}
            alt=""
            src={`${environment.cdnUrl}/images/emoji-nerdface.svg`}
          />
          {t(
            'The site does not need any personal details from you at this time.'
          )}
        </Header>
      )}
      {(isLoading && (
        <div>
          <FieldsWrapper>
            <FieldLabel>
              <Skeleton width="80%" height="20px" />
            </FieldLabel>
            <FieldComponent>
              <FieldInline>
                <FieldInline>
                  <Skeleton width="50px" height="40px" />
                </FieldInline>
                <Skeleton width="50px" height="40px" />
              </FieldInline>
              <FieldInline>
                <Skeleton width="80px" height="40px" />
              </FieldInline>
            </FieldComponent>
          </FieldsWrapper>

          <FieldsWrapper>
            <FieldLabel>
              <Skeleton width="80%" height="20px" />
            </FieldLabel>
            <Skeleton width="100%" height="40px" />
          </FieldsWrapper>

          <SubmitWrapper>
            <Skeleton width="100px" height="40px" />
          </SubmitWrapper>
        </div>
      )
      ) || (
          patient?.vaultId && isPatientInEU === false && (
            <p>
              {t(
                ('This information is unavailable due to your current location. '
                  + 'Please reach out to your Clinical Site Administrator for further '
                  + 'information. Error: SC00001')
              )}
            </p>
          )
        ) || (<form
          onSubmit={handleSubmit(onSubmit)}
          data-testid="patient-details-form"
        >
          {patient?.patientDetailsConfig?.dob?.visible && (
            <FieldsWrapper>
              <FieldLabel>{t('Date Of Birth')}</FieldLabel>
              <FieldComponent>
                <FieldInline>
                  <FieldInline>
                    <Controller
                      name="month"
                      control={control}
                      rules={{
                        required: patient?.patientDetailsConfig?.dob?.required,
                        maxLength: 2,
                        max: 12,
                      }}
                      render={({ field }) => (
                        <Input
                          type="number"
                          label={t('Month')}
                          mode="block"
                          placeholder={t("MM")}
                          required={patient?.patientDetailsConfig?.dob?.required}
                          requiredErrorMessage={t('This field is required')}
                          {...field}
                        />
                      )}
                    />
                  </FieldInline>
                  <Controller
                    name="day"
                    control={control}
                    rules={{
                      required: patient?.patientDetailsConfig?.dob?.required,
                      maxLength: 2,
                      max: 31,
                    }}
                    render={({ field }) => (
                      <Input
                        type="number"
                        label={t('Day')}
                        mode="block"
                        placeholder={t("DD")}
                        required={patient?.patientDetailsConfig?.dob?.required}
                        requiredErrorMessage={t('This field is required')}
                        {...field}
                      />
                    )}
                  />
                </FieldInline>
                <FieldInline>
                  <Controller
                    name="year"
                    control={control}
                    rules={{
                      required: patient?.patientDetailsConfig?.dob?.required,
                      maxLength: 4,
                      min: 1900,
                      max: 2022,
                    }}
                    render={({ field }) => (
                      <Input
                        type="number"
                        label={t('Year')}
                        mode="block"
                        placeholder={t("YYYY")}
                        required={patient?.patientDetailsConfig?.dob?.required}
                        requiredErrorMessage={t('This field is required')}
                        {...field}
                      />
                    )}
                  />
                </FieldInline>
              </FieldComponent>
            </FieldsWrapper>
          )}

          {patient?.patientDetailsConfig?.dob?.required &&
            !isDateValid({ year, month, day }) &&
            hasBeenTouched && (
              <Alert type="error" content={t('Please enter a valid date.')} />
            )}

          {patient?.patientDetailsConfig?.address?.visible && (
            <FieldsWrapper>
              <FieldLabel>{t('Address')}</FieldLabel>
              <AddressAutocomplete
                handleBlur={() => setAddressInteraction(true)}
                onSelectAddress={setAddress}
                initialValue={getInitialAddress(address || patient)}
                isRequired={patient?.patientDetailsConfig?.address?.required}
                value={parsedAddressValue}
                key={`${patient?.vaultId && (wasDeobfuscated ? 'plain-text' : 'masked')}-address`}
              />
            </FieldsWrapper>
          )}

          {addressInteraction &&
            patient?.patientDetailsConfig?.address?.required &&
            !address && (
              <Alert type="error" content={t('Please enter a valid address.')} />
            )}
          {isAnyFieldVisible(patient) && (
            <SubmitWrapper>
              <Button type="submit" disabled={!isValid}>
                {t('Submit')}
              </Button>
            </SubmitWrapper>
          )}
        </form>)}
    </ModalWrapper>
  );
};

export default PatientsDetailsFormPageComponent;
